import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { authService as auth } from "../utils/authService";
import { errorHandler } from "../components/shared/errorhandler";
import { logger } from "../logger-init";
import { appInsightsService } from "../services/appInsights.service";

interface RequestMetadata {
  startTime: number;
  correlationId: string;
}

declare module "axios" {
  interface InternalAxiosRequestConfig {
    metadata?: RequestMetadata;
  }
}

/**
 * HTTP Client Singleton
 * Centralized axios instance with interceptors for auth, error handling, and observability
 */
class HttpClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: "",
      timeout: 30000, // 30 seconds
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
    });

    this.setupInterceptors();
  }

  /**
   * Check if a URL is a local API endpoint that should use relative URLs
   */
  private isLocalApiEndpoint(url: string): boolean {
    // Local endpoints: /api/v1/*, /api/security/*, /ws/*, /api/v1/public/*
    return (
      url.startsWith("/api/v1/") ||
      url.startsWith("/api/security/") ||
      url.startsWith("/ws/") ||
      url.startsWith("/api/v1/public/")
    );
  }

  /**
   * Extract a clean API name from URL for tracking
   */
  private getApiName(url: string, method: string): string {
    // Remove query params and extract path
    const pathOnly = url.split("?")[0];
    // Remove UUIDs and IDs from path for aggregation
    const cleanPath = pathOnly
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "/:id")
      .replace(/\/\d+/g, "/:id");
    return `${method.toUpperCase()} ${cleanPath}`;
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - Add auth headers, correlation ID, and handle skyAdvisor routing
    this.instance.interceptors.request.use(
      async (config) => {
        try {
          const url = config.url || "";

          // Handle absolute URLs (http:// or https://) - clear baseURL to prevent double-prefixing
          if (url.startsWith("http://") || url.startsWith("https://")) {
            config.baseURL = "";
          }
          // CRITICAL: Force skyAdvisor endpoints to use relative URLs (local API)
          // This ensures /api/v1/* requests go to the local skyAdvisor backend
          // instead of the external Skypoint Cloud gateway
          else if (this.isLocalApiEndpoint(url)) {
            // Remove any baseURL for skyAdvisor endpoints - use relative path
            config.baseURL = "";
          }

          // Get auth headers from authService
          const authHeaders = await auth.getHeader();
          // Merge auth headers with existing headers
          Object.entries(authHeaders).forEach(([key, value]) => {
            if (config.headers) {
              config.headers[key] = value;
            }
          });

          // Add app base URL so the server can build correct links (e.g. email notifications)
          // window.location.origin is always correct regardless of environment
          if (config.headers && typeof window !== "undefined") {
            config.headers["X-App-Base-URL"] = window.location.origin;
          }

          // Add correlation ID for request tracking
          let correlationId = logger.getCorrelationId();
          if (!correlationId) {
            correlationId = logger.generateCorrelationId();
          }
          if (config.headers) {
            config.headers["X-Correlation-ID"] = correlationId;
          }

          // Add request metadata for timing
          config.metadata = {
            startTime: Date.now(),
            correlationId,
          };

          return config;
        } catch (error) {
          logger.error("Failed to get auth headers", error, {
            component: "HttpClient",
            action: "requestInterceptor",
          });
          // If we can't get auth headers, reject the request
          return Promise.reject(error);
        }
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor - Error handling with token refresh retry and performance tracking
    this.instance.interceptors.response.use(
      (response) => {
        // Track successful API calls
        const metadata = response.config.metadata;
        if (metadata) {
          const duration = Date.now() - metadata.startTime;
          const apiName = this.getApiName(
            response.config.url || "",
            response.config.method || "GET",
          );

          appInsightsService.trackApiCall(apiName, duration, true, response.status, {
            correlationId: metadata.correlationId,
          });

          // Log slow requests (> 2 seconds)
          if (duration > 2000) {
            logger.warn("Slow API request detected", {
              component: "HttpClient",
              url: response.config.url,
              method: response.config.method?.toUpperCase(),
              duration,
              status: response.status,
              correlationId: metadata.correlationId,
            });
          }
        }
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
        const correlationId = logger.getCorrelationId();

        // If we get a 401 and haven't retried yet, try to refresh token and retry
        // A phone sign-in has no refresh: a refused pass ends it.
        // The page is going to the sign-in page: no refresh to try, no error to show.
        if (error.response?.status === 401 && auth.phonePassRefused(error.response.data)) {
          return new Promise(() => undefined);
        }

        if (error.response?.status === 401 && !originalRequest._retry && originalRequest) {
          originalRequest._retry = true;

          try {
            logger.info("Token expired, attempting to refresh", {
              component: "HttpClient",
              action: "tokenRefresh",
              correlationId,
            });
            // Force a token refresh by calling getAccessToken
            await auth.getAccessToken();

            // Get fresh headers
            const authHeaders = await auth.getHeader();
            Object.entries(authHeaders).forEach(([key, value]) => {
              if (originalRequest.headers) {
                originalRequest.headers[key] = value;
              }
            });

            // Retry the original request with new token
            return this.instance(originalRequest);
          } catch (refreshError) {
            logger.error("Token refresh failed", refreshError, {
              component: "HttpClient",
              action: "tokenRefresh",
              correlationId,
            });
            // If refresh fails, user will be redirected to login
            return Promise.reject(refreshError);
          }
        }

        // Track failed API calls
        const metadata = (error.config as InternalAxiosRequestConfig)?.metadata;
        if (metadata) {
          const duration = Date.now() - metadata.startTime;
          const apiName = this.getApiName(error.config?.url || "", error.config?.method || "GET");

          appInsightsService.trackApiCall(apiName, duration, false, error.response?.status, {
            correlationId: metadata.correlationId,
            errorMessage: error.message,
          });
        }

        // Log API errors with full context
        if (error.response) {
          logger.error("API request failed", error, {
            component: "HttpClient",
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            status: error.response.status,
            statusText: error.response.statusText,
            correlationId,
          });

          // Integrate with existing errorHandler for other errors
          errorHandler(error.response);
        } else {
          // Network errors or request setup errors
          logger.error("Network or request error", error, {
            component: "HttpClient",
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            correlationId,
          });
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config);
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data, config);
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data, config);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url, config);
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.patch<T>(url, data, config);
  }

  /**
   * Download file as blob (for CSV exports, etc.)
   */
  async downloadBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.instance.get(url, {
      ...config,
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * POST request with blob response (for bulk downloads, etc.)
   */
  async postBlob(url: string, data?: any, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.instance.post(url, data, {
      ...config,
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * Upload file with multipart/form-data
   */
  async uploadFile<T = any>(
    url: string,
    file: File,
    fieldName = "file",
    additionalData?: Record<string, any>,
  ): Promise<AxiosResponse<T>> {
    const formData = new FormData();
    formData.append(fieldName, file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    return this.instance.post<T>(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  /**
   * POST request with FormData (for file uploads, etc.)
   */
  async postFormData<T = any>(
    url: string,
    formData: FormData,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        // Don't set Content-Type - browser will set it with boundary
      },
    });
  }

  /**
   * Get the underlying axios instance for advanced usage
   */
  getInstance(): AxiosInstance {
    return this.instance;
  }
}

// Export singleton instance
export const httpClient = new HttpClient();

/**
 * Public HTTP Client (No Authentication)
 * For pre-auth endpoints like signup, login, verify email, checkUserExists, etc.
 */
class PublicHttpClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: "",
      timeout: 120000,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Response interceptor - Basic error logging (no auth retry)
    this.instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response) {
          logger.error("Public API request failed", error, {
            component: "PublicHttpClient",
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            status: error.response.status,
          });
        } else {
          logger.error("Network error on public API", error, {
            component: "PublicHttpClient",
            url: error.config?.url,
          });
        }
        return Promise.reject(error);
      },
    );
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data, config);
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config);
  }
}

// Export public client for pre-auth endpoints
export const publicHttpClient = new PublicHttpClient();
